import apiClient from '../api';
import type { ProblemDataset, StarterCell, NotebookAssertion } from './codingAdminService';

export type { ProblemDataset, StarterCell, NotebookAssertion };

export interface StudentTestCase {
  id: string;
  input: string;
  expected_output: string;
  order_no: number;
  explanation?: string;
}

export interface StudentSubmission {
  id: string;
  status: string;
  score?: number;
  passed_tests: number;
  total_tests: number;
  execution_time_ms?: number;
  memory_used_mb?: number;
  error_message?: string;
  source_code: string;
  notebook_cells?: StarterCell[];
  assertion_results?: string;
  submitted_at: string;
}

export interface StudentCodingProblemListItem {
  id: string;
  title: string;
  slug: string;
  description: string;
  difficulty: 'EASY' | 'MEDIUM' | 'HARD';
  language: string;
  problem_type: 'FUNCTION' | 'STDIN_STDOUT' | 'NOTEBOOK';
  week_number?: number;
  day_number?: number;
  order_no: number;
  datasets_count?: number;
  datasets?: ProblemDataset[];
  notebook_assertions_count?: number;
  student_status: 'SOLVED' | 'ATTEMPTED' | 'UNSOLVED';
  submissions_count: number;
  best_execution_time_ms?: number;
  public_test_cases_count: number;
}

export interface StudentCodingProblemDetails {
  id: string;
  cohort_id: string;
  title: string;
  slug: string;
  description: string;
  difficulty: 'EASY' | 'MEDIUM' | 'HARD';
  language: string;
  problem_type: 'FUNCTION' | 'STDIN_STDOUT' | 'NOTEBOOK';
  starter_code: string;
  function_name?: string;
  input_format?: string;
  output_format?: string;
  constraints?: string;
  time_limit_ms: number;
  memory_limit_mb: number;
  week_number?: number;
  day_number?: number;
  datasets?: ProblemDataset[];
  starter_cells?: StarterCell[];
  notebook_assertions?: NotebookAssertion[];
  student_status: 'SOLVED' | 'ATTEMPTED' | 'UNSOLVED';
  public_test_cases: StudentTestCase[];
  recent_submissions: StudentSubmission[];
}

export interface RunCodeResultItem {
  test_case: number;
  status: 'passed' | 'wrong_answer' | 'runtime_error' | 'time_limit_exceeded' | 'internal_error';
  execution_time_ms: number;
  input?: string;
  expected_output?: string;
  actual_output?: string;
  stdout?: string;
  error_message?: string;
}

export interface RunCodeResponse {
  status: 'accepted' | 'wrong_answer' | 'runtime_error' | 'time_limit_exceeded' | 'internal_error';
  passed: number;
  total: number;
  execution_time_ms: number;
  first_error_message?: string;
  results: RunCodeResultItem[];
}

export interface SubmitCodeResultItem {
  test_case: number;
  is_hidden: boolean;
  status: 'passed' | 'wrong_answer' | 'runtime_error' | 'time_limit_exceeded' | 'internal_error';
  execution_time_ms: number;
  input?: string;
  expected_output?: string;
  actual_output?: string;
  stdout?: string;
  error_message?: string;
}

export interface SubmitCodeResponse {
  submission_id: string;
  status: 'accepted' | 'wrong_answer' | 'runtime_error' | 'time_limit_exceeded' | 'internal_error';
  passed: number;
  total: number;
  execution_time_ms?: number;
  error_message?: string;
  results: SubmitCodeResultItem[];
}

export interface WeekReadingMaterial {
  id: string;
  week_number: number;
  title: string;
  content: string; // Markdown content
}

export interface SubmitNotebookPayload {
  notebook_cells?: { id: string; type: string; content: string }[];
  source_code?: string;
  score?: number;
  status?: string;
  passed_tests?: number;
  total_tests?: number;
  assertion_results?: any;
}

export const codingStudentService = {
  getStudentProblems: () => {
    return apiClient.get<{ error: boolean; status: number; message: string; data: StudentCodingProblemListItem[] }>(
      '/students/coding-problems',
    );
  },

  getProblemDetails: (idOrSlug: string) => {
    return apiClient.get<{ error: boolean; status: number; message: string; data: StudentCodingProblemDetails }>(
      `/students/coding-problems/${idOrSlug}`,
    );
  },

  runCode: (payload: { problem_id: string; source_code: string; language?: string; custom_input?: string }) => {
    return apiClient.post<{ error: boolean; status: number; message: string; data: RunCodeResponse }>(
      '/students/coding-problems/run',
      payload,
    );
  },

  submitCode: (payload: { problem_id: string; source_code: string; language?: string }) => {
    return apiClient.post<{ error: boolean; status: number; message: string; data: SubmitCodeResponse }>(
      '/students/coding-problems/submit',
      payload,
    );
  },

  submitNotebookSolution: (idOrSlug: string, payload: SubmitNotebookPayload) => {
    return apiClient.post<{
      error: boolean;
      status: number;
      message: string;
      data: {
        submission_id: string;
        status: string;
        score: number;
        passed_tests: number;
        total_tests: number;
        submitted_at: string;
      };
    }>(`/students/coding-problems/${idOrSlug}/submit-notebook`, payload);
  },

  getProblemSubmissions: (problemId: string) => {
    return apiClient.get<{ error: boolean; status: number; message: string; data: StudentSubmission[] }>(
      `/students/coding-problems/${problemId}/submissions`,
    );
  },

  getWeekReadingMaterial: (weekNumber: number) => {
    return apiClient.get<{ error: boolean; status: number; message: string; data: WeekReadingMaterial }>(
      `/students/week-reading-material/${weekNumber}`,
    );
  },

  getSubmissionDetails: (submissionId: string) => {
    return apiClient.get<{ error: boolean; status: number; message: string; data: any }>(
      `/students/coding-submissions/${submissionId}`,
    );
  },

  getDatasetContent: (filename: string) => {
    const cleanFilename = filename.replace(/^.*[\\/]/, '');
    return apiClient.get<ArrayBuffer>(
      `/students/coding-problems/datasets/download/${cleanFilename}`,
      { responseType: 'arraybuffer' },
    );
  },
};

export default codingStudentService;
